export interface PasswordPolicyComplexity {
  description?: string;
  displayComplexity?: boolean;
  enableMinLength?: boolean;
  minLength?: number;
  needsLowercase?: boolean;
  needsUppercase?: boolean;
  needsNumeric?: boolean;
  needsSymbolic?: boolean;
  disallowSequentialOrRepetitiveChars?: boolean;
}

export type RequirementStatus = 'met' | 'unmet' | 'pending';

export interface PasswordRequirementDefinition {
  id: string;
  label: string;
  enabled: boolean;
  validate?: (password: string) => boolean;
}

export interface EvaluatedPasswordRequirement {
  id: string;
  label: string;
  status: RequirementStatus;
}

export const PASSWORD_MAX_LENGTH = 64;
export const PASSWORD_REQUIREMENTS_HEADING = 'Password Requirements (All must be met)';

function inferPasswordPolicyFromDescription(
  description: string,
): Partial<PasswordPolicyComplexity> {
  const trimmed = (description || '').trim();
  if (!trimmed) {
    return {};
  }

  const inferred: Partial<PasswordPolicyComplexity> = {};
  const lengthMatch = trimmed.match(/minimum length of (\d+) characters/i);

  if (lengthMatch) {
    inferred.enableMinLength = true;
    inferred.minLength = Number(lengthMatch[1]);
  }
  if (/lowercase/i.test(trimmed)) {
    inferred.needsLowercase = true;
  }
  if (/uppercase/i.test(trimmed)) {
    inferred.needsUppercase = true;
  }
  if (/\bnumber\b/i.test(trimmed)) {
    inferred.needsNumeric = true;
  }
  if (/special character/i.test(trimmed)) {
    inferred.needsSymbolic = true;
  }

  return inferred;
}

function hasStructuredPasswordPolicyFields(
  policy: PasswordPolicyComplexity,
): boolean {
  return Boolean(
    policy.enableMinLength
    || policy.needsLowercase
    || policy.needsUppercase
    || policy.needsNumeric
    || policy.needsSymbolic
    || policy.disallowSequentialOrRepetitiveChars,
  );
}

const VALID_SEQUENCE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function hasRepetitiveOrSequentialPattern(text: string): boolean {
  for (let i = 0; i < text.length - 2; i += 1) {
    const char = text[i];

    if (char === text[i + 1] && char === text[i + 2]) {
      return true;
    }

    if (i + 2 < text.length) {
      const nextChars = text.slice(i, i + 3);
      if (
        VALID_SEQUENCE_CHARS.includes(nextChars[1])
        && VALID_SEQUENCE_CHARS.includes(nextChars[2])
      ) {
        const charCode = char.charCodeAt(0);
        if (
          nextChars === String.fromCharCode(charCode)
            + String.fromCharCode(charCode + 1)
            + String.fromCharCode(charCode + 2)
          || nextChars === String.fromCharCode(charCode)
            + String.fromCharCode(charCode - 1)
            + String.fromCharCode(charCode - 2)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

export function normalizePasswordPolicyResponse(
  response: PasswordPolicyComplexity | string | null | undefined,
): PasswordPolicyComplexity | null {
  if (!response) {
    return null;
  }

  if (typeof response === 'string') {
    const description = response.trim();
    if (!description) {
      return null;
    }

    return {
      description,
      displayComplexity: true,
      ...inferPasswordPolicyFromDescription(description),
    };
  }

  if (response.displayComplexity === false) {
    return null;
  }

  const description = (response.description || '').trim();
  const normalized: PasswordPolicyComplexity = {
    description: response.description,
    displayComplexity: true,
    enableMinLength: response.enableMinLength,
    minLength: response.minLength,
    needsLowercase: response.needsLowercase,
    needsUppercase: response.needsUppercase,
    needsNumeric: response.needsNumeric,
    needsSymbolic: response.needsSymbolic,
    disallowSequentialOrRepetitiveChars: response.disallowSequentialOrRepetitiveChars,
  };

  if (!hasStructuredPasswordPolicyFields(normalized) && description) {
    Object.assign(normalized, inferPasswordPolicyFromDescription(description));
  }

  if (!hasStructuredPasswordPolicyFields(normalized) && !description) {
    return null;
  }

  return normalized;
}

export function buildPasswordRequirements(
  policy: PasswordPolicyComplexity,
): PasswordRequirementDefinition[] {
  const requirements: PasswordRequirementDefinition[] = [{
    id: 'minLength',
    label: `At least ${policy.minLength} characters`,
    enabled: Boolean(policy.enableMinLength),
    validate(password) {
      return password.length >= (policy.minLength || 0);
    },
  }, {
    id: 'lowercase',
    label: 'A lowercase letter',
    enabled: Boolean(policy.needsLowercase),
    validate(password) {
      return /[a-z]/.test(password);
    },
  }, {
    id: 'uppercase',
    label: 'An uppercase letter',
    enabled: Boolean(policy.needsUppercase),
    validate(password) {
      return /[A-Z]/.test(password);
    },
  }, {
    id: 'number',
    label: 'A number',
    enabled: Boolean(policy.needsNumeric),
    validate(password) {
      return /[0-9]/.test(password);
    },
  }, {
    id: 'specialChar',
    label: 'A special character (!, @, #, etc.)',
    enabled: Boolean(policy.needsSymbolic),
    validate(password) {
      return /[\W_]/.test(password);
    },
  }, {
    id: 'repetitiveOrSequential',
    label: 'Cannot contain repeated or sequential characters',
    enabled: Boolean(policy.disallowSequentialOrRepetitiveChars),
    validate(password) {
      return !hasRepetitiveOrSequentialPattern(password);
    },
  }];

  return requirements.filter((requirement) => requirement.enabled);
}

export function shouldShowPasswordRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
): boolean {
  if (!policy || policy.displayComplexity === false) {
    return false;
  }

  return buildPasswordRequirements(policy).length > 0;
}

export function evaluatePasswordRequirements(
  password: string,
  requirements: PasswordRequirementDefinition[],
): EvaluatedPasswordRequirement[] {
  const isEmpty = password.length === 0;

  return requirements.map((requirement) => {
    if (!requirement.validate || isEmpty) {
      return {
        id: requirement.id,
        label: requirement.label,
        status: 'pending',
      };
    }

    return {
      id: requirement.id,
      label: requirement.label,
      status: requirement.validate(password) ? 'met' : 'unmet',
    };
  });
}

export function getEvaluatedPasswordRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
  password: string,
): EvaluatedPasswordRequirement[] {
  if (!shouldShowPasswordRequirements(policy)) {
    return [];
  }

  const requirements = buildPasswordRequirements(policy as PasswordPolicyComplexity);
  return evaluatePasswordRequirements(password, requirements);
}

export function isPasswordSatisfyingClientCheckableRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
  password: string,
): boolean {
  const evaluated = getEvaluatedPasswordRequirements(policy, password);
  return evaluated.every((item) => item.status === 'met');
}
